import { useEffect, useRef } from "react";

const MAX_OFFSET = 32;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const animateCarouselItems = (container: HTMLElement): number => {
  const containerCentreX = container.scrollLeft + container.clientWidth / 2;
  const children = Array.from(container.children) as HTMLElement[];

  let closestIndex = 0;
  let closestDistance = Infinity;

  const itemData = children.map((el, i) => {
    const distance = Math.abs(
      containerCentreX - (el.offsetLeft + el.offsetWidth / 2)
    );
    const distanceFraction = Math.min(distance / el.offsetWidth, 1);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = i;
    }

    return { offset: MAX_OFFSET * distanceFraction, distanceFraction };
  });

  if (!prefersReducedMotion()) {
    children.forEach((el, i) => {
      const { offset, distanceFraction } = itemData[i];
      el.style.transform = `translateY(${offset}px)`;
      el.style.clipPath = `inset(0 0 ${2 * offset}px 0)`;
      el.classList.toggle("selectedItem", i === closestIndex);

      const label = el.querySelector(
        '[data-testid="lookLabel"]'
      ) as HTMLElement | null;
      if (label) {
        label.style.opacity = String(Math.max(0, 1 - distanceFraction));
      }
    });
  }

  return closestIndex;
};

export default function useCarouselAnimationScroll(
  selectedLookIndex: number,
  isOpen: boolean,
  onIndexChange: (index: number) => void
) {
  const containerRef = useRef<HTMLElement>(null);
  const rafId = useRef<number | null>(null);
  const isHookInitiatedScroll = useRef(false);

  useEffect(() => {
    if (!isOpen) return;

    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isHookInitiatedScroll.current) return;

      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        onIndexChange(animateCarouselItems(container));
      });
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [isOpen, onIndexChange]);

  useEffect(() => {
    if (!isOpen) return;

    const container = containerRef.current;
    if (!container) return;

    const centeredIndex = animateCarouselItems(container);
    if (centeredIndex === selectedLookIndex) {
      return;
    }

    const selectedElement = container.children[
      selectedLookIndex
    ] as HTMLElement;

    isHookInitiatedScroll.current = true;
    selectedElement?.scrollIntoView({
      behavior: "instant",
      inline: "center",
      block: "nearest",
    });

    const animationFrameId = requestAnimationFrame(() => {
      animateCarouselItems(container);
      isHookInitiatedScroll.current = false;
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [selectedLookIndex, isOpen]);

  return { containerRef };
}
